import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Save,
  RefreshCw,
  RotateCcw,
  SlidersHorizontal,
  DollarSign,
  ToggleLeft,
  List,
  Bell,
  Shield,
  Settings2,
  Info,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import applicationParametersService from '../../services/applicationParametersService'
import '../../styles/application-parameters.css'

const TABS = [
  { id: 'currency', label: 'Currency & FX', icon: DollarSign },
  { id: 'features', label: 'Feature switches', icon: ToggleLeft },
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'lists', label: 'Lists & CSV', icon: List },
  { id: 'subscriptions', label: 'Subscriptions', icon: SlidersHorizontal },
  { id: 'security', label: 'Security', icon: Shield },
  { id: 'notifications', label: 'Notifications', icon: Bell },
]

const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'UTC']
const DATE_FORMATS = ['DD-MMM-YYYY', 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']
const RATE_SOURCES = ['Manual', 'Bank', 'RBI', 'Accounting', 'Other']

const FEATURE_DEFS = [
  { key: 'subscriptionsModule', label: 'Subscriptions module', hint: 'Domains, cloud, SaaS and rental subscriptions' },
  { key: 'assetsModule', label: 'Assets module', hint: 'Acquisitions, assignments, maintenance and disposals' },
  { key: 'gstReminders', label: 'GST reminders', hint: 'Compliance reminder and summary emails' },
  { key: 'emailNotifications', label: 'Email notifications', hint: 'Outbound notification emails' },
  { key: 'inAppNotifications', label: 'In-app notifications', hint: 'Header bell and in-product alerts' },
  { key: 'subscriptionReminders', label: 'Subscription reminders', hint: 'Renewal, expiry and payment reminder job' },
  { key: 'procurementModule', label: 'Procurement', hint: 'PR, PO, GRN and vendor invoices' },
  { key: 'expensesModule', label: 'Expenses', hint: 'Claims, approvals and reimbursements' },
  { key: 'timesheetsModule', label: 'Timesheets', hint: 'Time entry and approvals' },
  { key: 'crmModule', label: 'CRM', hint: 'Leads, demos and commercial proposals' },
  { key: 'projectsModule', label: 'Projects', hint: 'Projects, tasks and billing' },
  { key: 'maintenanceMode', label: 'Maintenance mode', hint: 'Restrict non-admin access while changes are applied' },
  { key: 'allowSelfRegistration', label: 'Allow self-registration', hint: 'Off by default for enterprise tenants' },
  { key: 'experimentalAiInsights', label: 'Experimental AI insights', hint: 'Optional predictive widgets' },
  { key: 'showInfraIpToFinance', label: 'Show full infra IPs to finance', hint: 'Off: finance sees masked IPs only' },
  { key: 'requireSerialForAssetAssignment', label: 'Require serial for laptop assignment', hint: 'Blocks assignment once serials are known' },
]

const LIST_DEFS = [
  { key: 'allowedEmailDomains', label: 'Allowed email domains', hint: 'Comma-separated. Empty = no restriction.', placeholder: 'celeris.com, partner.in' },
  { key: 'infrastructureProviders', label: 'Infrastructure providers', hint: 'Used for filters and seed guidance.', placeholder: 'DigitalOcean, MongoDB, AWS' },
  { key: 'subscriptionCategories', label: 'Subscription categories', hint: 'Suggested categories for new subscriptions.' },
  { key: 'excludedCostCentres', label: 'Excluded cost centres', hint: 'Cost centres omitted from infra roll-ups.' },
  { key: 'financeAlertRecipients', label: 'Finance alert recipients', hint: 'Comma-separated emails for finance digests.' },
  { key: 'technicalAlertRecipients', label: 'Technical alert recipients', hint: 'Comma-separated emails for infra alerts.' },
  { key: 'blockedFileExtensions', label: 'Blocked upload extensions', hint: 'Comma-separated file extensions.' },
]

function emptyForm() {
  return {
    currency: {
      usdToInrRate: 83.5,
      rateEffectiveDate: '',
      rateSource: 'Manual',
      rateNotes: '',
      autoApplyToInfraCosts: true,
      displayCurrency: 'INR',
    },
    features: Object.fromEntries(FEATURE_DEFS.map((f) => [f.key, f.key === 'maintenanceMode' || f.key === 'allowSelfRegistration' || f.key === 'experimentalAiInsights' || f.key === 'showInfraIpToFinance' ? false : true])),
    general: {
      timezone: 'Asia/Kolkata',
      dateFormat: 'DD-MMM-YYYY',
      fiscalYearStartMonth: 4,
      defaultPageSize: 10,
      defaultLanguage: 'en-IN',
      companyShortName: '',
      supportContactEmail: '',
    },
    lists: Object.fromEntries(LIST_DEFS.map((l) => [l.key, ''])),
    subscriptions: {
      defaultReminderTimezone: 'Asia/Kolkata',
      reminderLookbackHours: 48,
      requireOwnerBeforeRenewal: true,
      infraCostIncludeUsageEstimates: true,
      highlightCriticalWithinDays: 30,
      defaultBillingCurrency: 'INR',
    },
    security: {
      sessionTimeoutMinutes: 480,
      passwordMinLength: 8,
      requireMfaForAdmins: false,
      lockoutAfterFailedAttempts: 5,
    },
    notifications: {
      digestEnabled: false,
      digestTime: '09:00',
      quietHoursEnabled: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
    },
  }
}

function mapFromApi(data) {
  const base = emptyForm()
  if (!data) return base
  ;['currency', 'features', 'general', 'lists', 'subscriptions', 'security', 'notifications'].forEach((section) => {
    base[section] = { ...base[section], ...(data[section] || {}) }
  })
  if (base.currency.rateEffectiveDate) {
    const d = new Date(base.currency.rateEffectiveDate)
    if (!Number.isNaN(d.getTime())) {
      base.currency.rateEffectiveDate = d.toISOString().slice(0, 10)
    }
  } else {
    base.currency.rateEffectiveDate = ''
  }
  return base
}

function parseCsvPreview(value) {
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 12)
}

function Toggle({ checked, disabled, onChange, id }) {
  return (
    <label className="ap-switch" htmlFor={id}>
      <input id={id} type="checkbox" checked={!!checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="ap-switch__track" />
    </label>
  )
}

const ApplicationParameters = () => {
  const navigate = useNavigate()
  const [tab, setTab] = useState('currency')
  const [form, setForm] = useState(emptyForm())
  const [initial, setInitial] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [canEdit, setCanEdit] = useState(false)

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await applicationParametersService.get()
      if (!res.success) {
        toast.error(res.message || 'Failed to load parameters')
        return
      }
      const mapped = mapFromApi(res.data)
      setForm(mapped)
      setInitial(mapped)
      setCanEdit(Boolean(res.meta?.canEdit))
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load application parameters')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const setSection = (section, patch) => {
    setForm((prev) => ({
      ...prev,
      [section]: { ...prev[section], ...patch },
    }))
  }

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('Only administrators can save application parameters')
      return
    }
    const rate = Number(form.currency.usdToInrRate)
    if (!Number.isFinite(rate) || rate <= 0) {
      toast.error('USD to INR rate must be a positive number')
      setTab('currency')
      return
    }
    try {
      setSaving(true)
      const payload = {
        ...form,
        currency: {
          ...form.currency,
          usdToInrRate: rate,
          rateEffectiveDate: form.currency.rateEffectiveDate || null,
        },
      }
      const res = await applicationParametersService.save(payload)
      if (!res.success) {
        toast.error(res.message || 'Save failed')
        return
      }
      const mapped = mapFromApi(res.data)
      setForm(mapped)
      setInitial(mapped)
      toast.success('Application parameters saved')
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (initial) setForm(initial)
  }

  const sampleUsd = 100
  const sampleInr = (Number(form.currency.usdToInrRate) || 0) * sampleUsd
  const enabledFeatures = FEATURE_DEFS.filter((f) => form.features[f.key]).length

  if (loading) {
    return <div className="ap-page ap-loading">Loading application parameters…</div>
  }

  return (
    <div className="ap-page">
      <Breadcrumb
        items={[
          { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
          { label: 'Application Parameters', isCurrent: true },
        ]}
      />

      <div className="ap-hero">
        <div className="ap-hero__main">
          <p className="ap-eyebrow">Settings · Tenant configuration</p>
          <h1>Application Parameters</h1>
          <p className="ap-hero__sub">
            Central switches, lists and conversion rates for Celeris ERP. The USD → INR rate drives infrastructure cost roll-ups for DigitalOcean, MongoDB and other USD subscriptions when a per-record rate is not set.
          </p>
        </div>
        <div className="ap-fx-card">
          <div className="ap-fx-card__label">USD → INR</div>
          <div className="ap-fx-card__rate">
            1 USD = ₹{(Number(form.currency.usdToInrRate) || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 })}
          </div>
          <div className="ap-fx-card__meta">
            {form.currency.autoApplyToInfraCosts ? 'Auto-applied to infra costs' : 'Manual apply only'}
            {form.currency.rateSource ? ` · ${form.currency.rateSource}` : ''}
          </div>
        </div>
      </div>

      <div className="ap-stat-row">
        <div className="ap-stat">
          <div className="ap-stat__label">Features on</div>
          <div className="ap-stat__value">{enabledFeatures}/{FEATURE_DEFS.length}</div>
        </div>
        <div className="ap-stat">
          <div className="ap-stat__label">Sample $100 → INR</div>
          <div className="ap-stat__value">₹{sampleInr.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
        <div className="ap-stat">
          <div className="ap-stat__label">Timezone</div>
          <div className="ap-stat__value" style={{ fontSize: '0.95rem' }}>{form.general.timezone}</div>
        </div>
        <div className="ap-stat">
          <div className="ap-stat__label">Status</div>
          <div className="ap-stat__value" style={{ fontSize: '0.95rem' }}>{dirty ? 'Unsaved' : 'Saved'}</div>
        </div>
      </div>

      {!canEdit && (
        <div className="ap-banner ap-banner--warn">
          <AlertTriangle size={18} />
          <span>You can view parameters but only administrators can save changes.</span>
        </div>
      )}

      <div className="ap-toolbar">
        <div className="ap-tabs">
          {TABS.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                type="button"
                className={`ap-tabs__btn${tab === t.id ? ' is-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                <Icon size={15} /> {t.label}
              </button>
            )
          })}
        </div>
        <div className="ap-actions">
          <button type="button" className="ap-btn ap-btn--ghost" onClick={load} disabled={saving}>
            <RefreshCw size={15} /> Reload
          </button>
          <button type="button" className="ap-btn ap-btn--ghost" onClick={handleReset} disabled={!dirty || saving}>
            <RotateCcw size={15} /> Discard
          </button>
          <button type="button" className="ap-btn ap-btn--primary" onClick={handleSave} disabled={!canEdit || saving || !dirty}>
            <Save size={15} /> {saving ? 'Saving…' : 'Save parameters'}
          </button>
        </div>
      </div>

      {tab === 'currency' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><DollarSign size={18} /> Currency & infrastructure FX</h3>
              <p className="ap-card__hint">Used when converting USD DigitalOcean / MongoDB and other foreign-currency infrastructure costs to INR.</p>
            </div>
          </div>
          <div className="ap-banner">
            <Info size={16} />
            <span>
              Per-subscription <strong>exchangeRate</strong> still wins when set to a real rate. This company rate applies when currency is USD and the record rate is blank or 1.
            </span>
          </div>
          <div className="ap-grid">
            <div className="ap-field">
              <label htmlFor="usdToInrRate">USD to INR conversion rate *</label>
              <input
                id="usdToInrRate"
                type="number"
                className="ap-input"
                step="0.0001"
                min="0.0001"
                value={form.currency.usdToInrRate}
                disabled={!canEdit}
                onChange={(e) => setSection('currency', { usdToInrRate: e.target.value })}
              />
              <span className="ap-help">Example: 83.50 means $1 = ₹83.50</span>
            </div>
            <div className="ap-field">
              <label htmlFor="rateEffectiveDate">Rate effective date</label>
              <input
                id="rateEffectiveDate"
                type="date"
                className="ap-input"
                value={form.currency.rateEffectiveDate || ''}
                disabled={!canEdit}
                onChange={(e) => setSection('currency', { rateEffectiveDate: e.target.value })}
              />
            </div>
            <div className="ap-field">
              <label htmlFor="rateSource">Rate source</label>
              <select
                id="rateSource"
                className="ap-select"
                value={form.currency.rateSource}
                disabled={!canEdit}
                onChange={(e) => setSection('currency', { rateSource: e.target.value })}
              >
                {RATE_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="ap-field">
              <label htmlFor="displayCurrency">Default display currency</label>
              <input
                id="displayCurrency"
                className="ap-input"
                maxLength={3}
                value={form.currency.displayCurrency}
                disabled={!canEdit}
                onChange={(e) => setSection('currency', { displayCurrency: e.target.value.toUpperCase() })}
              />
            </div>
          </div>
          <div className="ap-field">
            <label htmlFor="rateNotes">Rate notes</label>
            <textarea
              id="rateNotes"
              className="ap-textarea"
              rows={2}
              value={form.currency.rateNotes || ''}
              disabled={!canEdit}
              onChange={(e) => setSection('currency', { rateNotes: e.target.value })}
              placeholder="e.g. Finance board rate for Sep 2026 infra close"
            />
          </div>
          <div className="ap-toggle">
            <div className="ap-toggle__text">
              <strong>Auto-apply to infrastructure costs</strong>
              <span>Use this rate for USD subscriptions and cloud infra roll-ups when no verified per-record rate exists.</span>
            </div>
            <Toggle
              id="autoApplyToInfraCosts"
              checked={form.currency.autoApplyToInfraCosts}
              disabled={!canEdit}
              onChange={(v) => setSection('currency', { autoApplyToInfraCosts: v })}
            />
          </div>
        </div>
      )}

      {tab === 'features' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><ToggleLeft size={18} /> Feature switches</h3>
              <p className="ap-card__hint">Turn modules and behaviours on or off for this company tenant.</p>
            </div>
          </div>
          <div className="ap-toggle-list">
            {FEATURE_DEFS.map((f) => (
              <div className="ap-toggle" key={f.key}>
                <div className="ap-toggle__text">
                  <strong>{f.label}</strong>
                  <span>{f.hint}</span>
                </div>
                <Toggle
                  id={`feat-${f.key}`}
                  checked={form.features[f.key]}
                  disabled={!canEdit}
                  onChange={(v) => setSection('features', { [f.key]: v })}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'general' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><Settings2 size={18} /> General defaults</h3>
              <p className="ap-card__hint">Timezone, formats and tenant display defaults.</p>
            </div>
          </div>
          <div className="ap-grid">
            <div className="ap-field">
              <label>Timezone</label>
              <select
                className="ap-select"
                value={form.general.timezone}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { timezone: e.target.value })}
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="ap-field">
              <label>Date format</label>
              <select
                className="ap-select"
                value={form.general.dateFormat}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { dateFormat: e.target.value })}
              >
                {DATE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div className="ap-field">
              <label>Fiscal year start month</label>
              <input
                type="number"
                className="ap-input"
                min={1}
                max={12}
                value={form.general.fiscalYearStartMonth}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { fiscalYearStartMonth: Number(e.target.value) || 4 })}
              />
              <span className="ap-help">India default: 4 (April)</span>
            </div>
            <div className="ap-field">
              <label>Default page size</label>
              <input
                type="number"
                className="ap-input"
                min={5}
                max={100}
                value={form.general.defaultPageSize}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { defaultPageSize: Number(e.target.value) || 10 })}
              />
            </div>
            <div className="ap-field">
              <label>Company short name</label>
              <input
                className="ap-input"
                value={form.general.companyShortName}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { companyShortName: e.target.value })}
                placeholder="Celeris"
              />
            </div>
            <div className="ap-field">
              <label>Support contact email</label>
              <input
                type="email"
                className="ap-input"
                value={form.general.supportContactEmail}
                disabled={!canEdit}
                onChange={(e) => setSection('general', { supportContactEmail: e.target.value })}
              />
            </div>
          </div>
        </div>
      )}

      {tab === 'lists' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><List size={18} /> Comma-separated lists</h3>
              <p className="ap-card__hint">Flexible multi-value parameters. Separate entries with commas.</p>
            </div>
          </div>
          {LIST_DEFS.map((l) => {
            const chips = parseCsvPreview(form.lists[l.key])
            return (
              <div className="ap-field" key={l.key}>
                <label htmlFor={l.key}>{l.label}</label>
                <textarea
                  id={l.key}
                  className="ap-textarea"
                  rows={2}
                  value={form.lists[l.key] || ''}
                  disabled={!canEdit}
                  placeholder={l.placeholder}
                  onChange={(e) => setSection('lists', { [l.key]: e.target.value })}
                />
                <span className="ap-help">{l.hint}</span>
                {chips.length > 0 && (
                  <div className="ap-preview">
                    {chips.map((c) => <span className="ap-chip" key={c}>{c}</span>)}
                    {String(form.lists[l.key] || '').split(',').filter((s) => s.trim()).length > chips.length && (
                      <span className="ap-chip">+ more</span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {tab === 'subscriptions' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><SlidersHorizontal size={18} /> Subscriptions & infra</h3>
              <p className="ap-card__hint">Defaults for renewals, reminders and infrastructure cost behaviour.</p>
            </div>
          </div>
          <div className="ap-grid">
            <div className="ap-field">
              <label>Default reminder timezone</label>
              <select
                className="ap-select"
                value={form.subscriptions.defaultReminderTimezone}
                disabled={!canEdit}
                onChange={(e) => setSection('subscriptions', { defaultReminderTimezone: e.target.value })}
              >
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div className="ap-field">
              <label>Reminder lookback (hours)</label>
              <input
                type="number"
                className="ap-input"
                min={1}
                max={168}
                value={form.subscriptions.reminderLookbackHours}
                disabled={!canEdit}
                onChange={(e) => setSection('subscriptions', { reminderLookbackHours: Number(e.target.value) || 48 })}
              />
            </div>
            <div className="ap-field">
              <label>Highlight critical within (days)</label>
              <input
                type="number"
                className="ap-input"
                min={1}
                max={90}
                value={form.subscriptions.highlightCriticalWithinDays}
                disabled={!canEdit}
                onChange={(e) => setSection('subscriptions', { highlightCriticalWithinDays: Number(e.target.value) || 30 })}
              />
            </div>
            <div className="ap-field">
              <label>Default billing currency</label>
              <input
                className="ap-input"
                maxLength={3}
                value={form.subscriptions.defaultBillingCurrency}
                disabled={!canEdit}
                onChange={(e) => setSection('subscriptions', { defaultBillingCurrency: e.target.value.toUpperCase() })}
              />
            </div>
          </div>
          <div className="ap-toggle-list">
            <div className="ap-toggle">
              <div className="ap-toggle__text">
                <strong>Require owner before renewal completion</strong>
                <span>Blocks marking renewed until business/technical ownership is assigned.</span>
              </div>
              <Toggle
                id="requireOwnerBeforeRenewal"
                checked={form.subscriptions.requireOwnerBeforeRenewal}
                disabled={!canEdit}
                onChange={(v) => setSection('subscriptions', { requireOwnerBeforeRenewal: v })}
              />
            </div>
            <div className="ap-toggle">
              <div className="ap-toggle__text">
                <strong>Include usage estimates in infra cost</strong>
                <span>Usage-based DigitalOcean / MongoDB estimates feed committed cost when enabled.</span>
              </div>
              <Toggle
                id="infraCostIncludeUsageEstimates"
                checked={form.subscriptions.infraCostIncludeUsageEstimates}
                disabled={!canEdit}
                onChange={(v) => setSection('subscriptions', { infraCostIncludeUsageEstimates: v })}
              />
            </div>
          </div>
        </div>
      )}

      {tab === 'security' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><Shield size={18} /> Security parameters</h3>
              <p className="ap-card__hint">Session and access policy defaults for this tenant.</p>
            </div>
          </div>
          <div className="ap-grid">
            <div className="ap-field">
              <label>Session timeout (minutes)</label>
              <input
                type="number"
                className="ap-input"
                min={15}
                max={10080}
                value={form.security.sessionTimeoutMinutes}
                disabled={!canEdit}
                onChange={(e) => setSection('security', { sessionTimeoutMinutes: Number(e.target.value) || 480 })}
              />
            </div>
            <div className="ap-field">
              <label>Password minimum length</label>
              <input
                type="number"
                className="ap-input"
                min={6}
                max={128}
                value={form.security.passwordMinLength}
                disabled={!canEdit}
                onChange={(e) => setSection('security', { passwordMinLength: Number(e.target.value) || 8 })}
              />
            </div>
            <div className="ap-field">
              <label>Lockout after failed attempts</label>
              <input
                type="number"
                className="ap-input"
                min={0}
                max={20}
                value={form.security.lockoutAfterFailedAttempts}
                disabled={!canEdit}
                onChange={(e) => setSection('security', { lockoutAfterFailedAttempts: Number(e.target.value) || 0 })}
              />
              <span className="ap-help">0 = disabled</span>
            </div>
          </div>
          <div className="ap-toggle">
            <div className="ap-toggle__text">
              <strong>Require MFA for administrators</strong>
              <span>Policy flag for rollout — enforce when MFA providers are configured.</span>
            </div>
            <Toggle
              id="requireMfaForAdmins"
              checked={form.security.requireMfaForAdmins}
              disabled={!canEdit}
              onChange={(v) => setSection('security', { requireMfaForAdmins: v })}
            />
          </div>
        </div>
      )}

      {tab === 'notifications' && (
        <div className="ap-card">
          <div className="ap-card__head">
            <div>
              <h3><Bell size={18} /> Notification defaults</h3>
              <p className="ap-card__hint">Digest and quiet-hours preferences for the tenant.</p>
            </div>
          </div>
          <div className="ap-toggle-list">
            <div className="ap-toggle">
              <div className="ap-toggle__text">
                <strong>Daily digest enabled</strong>
                <span>Batch non-urgent alerts into a morning digest.</span>
              </div>
              <Toggle
                id="digestEnabled"
                checked={form.notifications.digestEnabled}
                disabled={!canEdit}
                onChange={(v) => setSection('notifications', { digestEnabled: v })}
              />
            </div>
            <div className="ap-toggle">
              <div className="ap-toggle__text">
                <strong>Quiet hours</strong>
                <span>Suppress non-critical emails overnight.</span>
              </div>
              <Toggle
                id="quietHoursEnabled"
                checked={form.notifications.quietHoursEnabled}
                disabled={!canEdit}
                onChange={(v) => setSection('notifications', { quietHoursEnabled: v })}
              />
            </div>
          </div>
          <div className="ap-grid" style={{ marginTop: 12 }}>
            <div className="ap-field">
              <label>Digest time</label>
              <input
                type="time"
                className="ap-input"
                value={form.notifications.digestTime}
                disabled={!canEdit}
                onChange={(e) => setSection('notifications', { digestTime: e.target.value })}
              />
            </div>
            <div className="ap-field">
              <label>Quiet hours start</label>
              <input
                type="time"
                className="ap-input"
                value={form.notifications.quietHoursStart}
                disabled={!canEdit}
                onChange={(e) => setSection('notifications', { quietHoursStart: e.target.value })}
              />
            </div>
            <div className="ap-field">
              <label>Quiet hours end</label>
              <input
                type="time"
                className="ap-input"
                value={form.notifications.quietHoursEnd}
                disabled={!canEdit}
                onChange={(e) => setSection('notifications', { quietHoursEnd: e.target.value })}
              />
            </div>
          </div>
          {dirty && (
            <div className="ap-banner" style={{ marginTop: 8 }}>
              <CheckCircle2 size={16} />
              <span>You have unsaved changes. Save to apply across the application.</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ApplicationParameters
